import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";
import { buildHead, PAGE_SEO, pickLang, IN_LANGUAGE, SITE_URL, localized } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { fetchPublishedArticles } from "@/lib/publicData";

const BLOG_COPY = {
  es: {
    kicker: "Blog",
    h1: "Ideas para tu próximo movimiento inmobiliario",
    intro: "Análisis, tips y guías prácticas para comprar, vender o alquilar en Paraguay.",
    empty: "Aún no hay artículos publicados. Volvé pronto.",
    read: "Leer artículo",
    locale: "es-PY",
  },
  en: {
    kicker: "Blog",
    h1: "Insights for your next real estate move in Paraguay",
    intro: "Analysis, tips and practical guides to buy, sell or rent property in Paraguay.",
    empty: "No articles published yet. Come back soon.",
    read: "Read article",
    locale: "en-US",
  },
  pt: {
    kicker: "Blog",
    h1: "Ideias para o seu próximo passo imobiliário no Paraguai",
    intro: "Análises, dicas e guias práticos para comprar, vender ou alugar imóveis no Paraguai.",
    empty: "Ainda não há artigos publicados. Volte em breve.",
    read: "Ler artigo",
    locale: "pt-BR",
  },
} as const;

export const Route = createFileRoute("/_public/blog/")({
  head: ({ match }) => {
    const lang = pickLang(match.search);
    const copy = PAGE_SEO["blog"]![lang];
    const { meta, links, url } = buildHead({ lang, path: "/blog", ...copy });
    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: copy.title,
            description: copy.description,
            url,
            inLanguage: IN_LANGUAGE[lang],
            publisher: {
              "@type": "RealEstateAgent",
              name: "Dahiana Marín Bienes Raíces",
              url: SITE_URL,
            },
          }),
        },
      ],
    };
  },
  loader: () => fetchPublishedArticles(),
  component: BlogList,
});


function BlogList() {
  const { lang } = useI18n();
  const copy = BLOG_COPY[lang];
  const initial = Route.useLoaderData();
  const { data, isLoading } = useQuery({
    queryKey: ["blog", "published"],
    queryFn: fetchPublishedArticles,
    initialData: initial as Awaited<ReturnType<typeof fetchPublishedArticles>>,
  });


  return (
    <div className="mx-auto max-w-6xl px-4 py-14 md:px-6">
      <header className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">{copy.kicker}</p>
        <h1 className="mt-3 font-serif text-4xl text-foreground md:text-5xl">
          {copy.h1}
        </h1>
        <p className="mt-4 text-muted-foreground">
          {copy.intro}
        </p>
      </header>

      <div className="mt-12">
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-72 animate-pulse rounded-xl bg-secondary/60" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
            {copy.empty}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.map((a) => (
              <Link
                key={a.id}
                to="/blog/$slug"
                params={{ slug: a.slug }}
                className="group overflow-hidden rounded-xl border border-border bg-card transition hover:shadow-card"
              >
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  {a.cover_url ? (
                    <img
                      src={a.cover_url}
                      alt={localized(a, "title", lang) ?? ""}
                      width={1200}
                      height={670}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />

                  ) : (
                    <div className="grid h-full w-full place-items-center font-serif text-4xl text-muted-foreground/40">
                      D
                    </div>
                  )}
                </div>
                <div className="p-5">
                  {a.published_at && (
                    <time className="text-xs uppercase tracking-wider text-muted-foreground">
                      {new Date(a.published_at).toLocaleDateString(copy.locale, {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </time>
                  )}
                  <h2 className="mt-2 font-serif text-xl text-foreground">{localized(a, "title", lang)}</h2>
                  {localized(a, "excerpt", lang) && (
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                      {localized(a, "excerpt", lang)}
                    </p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    {copy.read} <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
